import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import { Loader, Button } from "@mantine/core";
import { IconChevronRight, IconPlus } from "@tabler/icons-react";
import AddRepModal from "./AddRepModal";
import "./CompanyDownlineCard.css";

const AVATAR_COLORS = [
  "#7a9a3c",
  "#8b5e3c",
  "#7b5ea7",
  "#3c8b5e",
  "#3c5ea7",
  "#a7663c",
  "#5e3ca7",
  "#a73c5e",
];

function getAvatarColor(id) {
  let h = (id * 2654435761) >>> 0;
  h ^= h >> 13;
  h = (h * 2246822519) >>> 0;
  h ^= h >> 16;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function getInitials(firstName = "", lastName = "") {
  return `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase();
}

function formatRole(role = "") {
  return role.replace(/_/g, " ");
}

// Build a flat index {rep_id: Rep} from the nested tree
function buildIndex(reps, index = {}) {
  for (const rep of reps) {
    index[rep.rep_id] = rep;
    if (rep.reps?.length) buildIndex(rep.reps, index);
  }
  return index;
}

// Count total reps in subtree (not including the root rep itself)
function countSubtree(rep) {
  if (!rep.reps?.length) return 0;
  return rep.reps.reduce((sum, r) => sum + 1 + countSubtree(r), 0);
}

// Count managers in subtree
function countManagers(reps) {
  let count = 0;
  for (const rep of reps) {
    if (rep.is_manager || rep.role_name?.toUpperCase().includes("MANAGER"))
      count++;
    if (rep.reps?.length) count += countManagers(rep.reps);
  }
  return count;
}

function CompanyDownlineCard() {
  const { getVerifiedJWT } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [topLevelReps, setTopLevelReps] = useState([]);
  const [repIndex, setRepIndex] = useState({});
  const [summaryMetrics, setSummaryMetrics] = useState(null);
  // path is an array of rep_ids representing the drill-in stack
  const [path, setPath] = useState([]);
  const [showAddRep, setShowAddRep] = useState(false);

  const fetchDownline = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setError("Authentication failed");
        return;
      }
      const response = await fetch(
        getApiEndpoint("/v1/company-downline/reps-by-manager"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (response.ok) {
        const result = await response.json();
        const reps = result.data?.reps || [];
        setTopLevelReps(reps);
        setRepIndex(buildIndex(reps));
        setSummaryMetrics(result.data?.summary_metrics || null);
      } else {
        setError("Failed to load downline");
      }
    } catch {
      setError("Failed to load downline");
    } finally {
      setLoading(false);
    }
  }, [getVerifiedJWT]);

  useEffect(() => {
    fetchDownline();
  }, [fetchDownline]);

  // Derive the current view's children and summary from path
  const { currentReps, summary } = useMemo(() => {
    if (path.length === 0) {
      // Root view — use top-level reps and compute summary
      const totalDownline = topLevelReps.reduce(
        (sum, r) => sum + 1 + countSubtree(r),
        0
      );
      const managerCount = countManagers(topLevelReps);
      return {
        currentReps: topLevelReps,
        summary: {
          total_downline: summaryMetrics?.total_reps ?? totalDownline,
          new_count: summaryMetrics?.new_count ?? 0,
          manager_count: managerCount,
          direct_count: topLevelReps.length,
        },
      };
    }

    // Drilled in — get focused rep's children
    const focusId = path[path.length - 1];
    const focusRep = repIndex[focusId];
    const kids = focusRep?.reps || [];
    const totalDownline = kids.reduce((sum, r) => sum + 1 + countSubtree(r), 0);
    const managerCount = countManagers(kids);
    return {
      currentReps: kids,
      summary: {
        total_downline: totalDownline,
        new_count: 0,
        manager_count: managerCount,
        direct_count: kids.length,
      },
    };
  }, [path, topLevelReps, repIndex, summaryMetrics]);

  const handleRepClick = rep => {
    if (!rep.reps?.length) return;
    setPath(prev => [...prev, rep.rep_id]);
  };

  const handleBreadcrumbClick = index => {
    if (index === -1) {
      setPath([]);
    } else {
      setPath(prev => prev.slice(0, index + 1));
    }
  };

  const isRoot = path.length === 0;
  const currentTitle = isRoot
    ? "Company Downline"
    : (() => {
        const rep = repIndex[path[path.length - 1]];
        return rep
          ? `${rep.first_name || ""} ${rep.last_name || ""}`.trim()
          : "Downline";
      })();

  return (
    <div className="company-downline-card">
      <div className="cdl-header">
        <div className="cdl-header-left">
          <span className="cdl-title">{currentTitle}</span>
          {isRoot && <span className="cdl-title-caret">▾</span>}
        </div>
        <div className="cdl-header-right">
          {isRoot && summary.direct_count > 0 && (
            <div className="cdl-direct-badge">
              <span className="cdl-direct-badge-count">
                {summary.direct_count}
              </span>
              <span className="cdl-direct-badge-label">DIRECT DOWNLINE</span>
            </div>
          )}
          <Button
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() => setShowAddRep(true)}
          >
            Add Rep
          </Button>
        </div>
      </div>

      {!isRoot && (
        <div className="cdl-breadcrumb">
          <button
            className="cdl-breadcrumb-btn"
            onClick={() => handleBreadcrumbClick(-1)}
          >
            Company Downline
          </button>
          {path.map((repId, i) => {
            const rep = repIndex[repId];
            const name = rep
              ? `${rep.first_name || ""} ${rep.last_name || ""}`.trim()
              : String(repId);
            const isCurrent = i === path.length - 1;
            return (
              <React.Fragment key={repId}>
                <span className="cdl-breadcrumb-sep">
                  <IconChevronRight size={12} />
                </span>
                <button
                  className={`cdl-breadcrumb-btn${isCurrent ? " cdl-breadcrumb-current" : ""}`}
                  onClick={() => !isCurrent && handleBreadcrumbClick(i)}
                >
                  {name}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}

      <div className="cdl-stats">
        <div className="cdl-stat">
          <div className="cdl-stat-value">{summary.total_downline}</div>
          <div className="cdl-stat-label">DOWNLINE</div>
        </div>
        <div className="cdl-stat">
          <div className="cdl-stat-value green">{summary.new_count}</div>
          <div className="cdl-stat-label">NEW</div>
        </div>
        <div className="cdl-stat">
          <div className="cdl-stat-value">{summary.manager_count}</div>
          <div className="cdl-stat-label">MANAGERS</div>
        </div>
        <div className="cdl-stat">
          <div className="cdl-stat-value orange">{summary.direct_count}</div>
          <div className="cdl-stat-label">DIRECT</div>
        </div>
      </div>

      {loading ? (
        <div className="cdl-loading">
          <Loader size="sm" />
        </div>
      ) : error ? (
        <div className="cdl-error-msg">{error}</div>
      ) : (
        <div>
          <div className="cdl-table-header">
            <div className="cdl-th">REP</div>
            <div className="cdl-th cdl-th-new">NEW</div>
            <div className="cdl-th cdl-th-total">TOTAL</div>
          </div>

          {currentReps.length === 0 ? (
            <div className="cdl-empty">No reps in this downline</div>
          ) : (
            currentReps.map((rep, i) => {
              const name =
                `${rep.first_name || ""} ${rep.last_name || ""}`.trim() ||
                "Unknown";
              const initials = getInitials(rep.first_name, rep.last_name);
              const avatarColor = getAvatarColor(rep.rep_id ?? i);
              const hasChildren = !!rep.reps?.length;
              const totalCount = countSubtree(rep);
              const directCount = rep.reps?.length ?? 0;

              return (
                <div
                  key={rep.rep_id ?? i}
                  className={`cdl-rep-row${hasChildren ? " clickable" : ""}`}
                  onClick={() => hasChildren && handleRepClick(rep)}
                >
                  <div className="cdl-rep-info">
                    <div
                      className="cdl-avatar"
                      style={{ background: avatarColor }}
                      onClick={e => {
                        e.stopPropagation();
                        navigate(`/reps/${rep.rep_id}`);
                      }}
                    >
                      {initials}
                    </div>
                    <div className="cdl-rep-text">
                      <div className="cdl-rep-name">{name}</div>
                      <div className="cdl-rep-sub">
                        {rep.region && <span>{rep.region}</span>}
                        {rep.region && rep.role_name && <span> · </span>}
                        {rep.role_name && (
                          <span>{formatRole(rep.role_name)}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="cdl-cell-new">
                    <span className="cdl-dash">—</span>
                    <div className="cdl-cell-sublabel">NEW</div>
                  </div>

                  <div className="cdl-cell-total">
                    <div className="cdl-total-content">
                      {totalCount > 0 ? (
                        <>
                          <span className="cdl-total-value">{totalCount}</span>
                          {directCount > 0 && (
                            <div className="cdl-total-direct">
                              {directCount} DIRECT
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="cdl-dash">—</span>
                      )}
                    </div>
                    {hasChildren && (
                      <IconChevronRight size={16} className="cdl-chevron" />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
      <AddRepModal
        opened={showAddRep}
        onClose={() => setShowAddRep(false)}
        onSuccess={fetchDownline}
      />
    </div>
  );
}

export default CompanyDownlineCard;
